import { Component } from '../../core/component.js';
import { store } from '../../store.js';
import { api } from '../../api.js';
import { eventBus } from '../../events.js';
import { escapeHtml } from '../../core/utils.js';
import { Sidebar } from './sidebar.js';
import { Header } from './header.js';
import { BottomNav } from './bottom-nav.js';
import { CommandPalette } from './command-palette.js';
import { AssistantPanel } from '../assistant/assistant-panel.js';

const LIMIT_LABELS = {
    staff: 'мастеров',
    clients: 'клиентов',
    services: 'услуг',
    branches: 'филиалов',
    sms: 'SMS',
};

export class AppLayout extends Component {
    constructor() {
        super();
        this.el.id = 'app';
        this.sidebar = null;
        this.header = null;
        this.bottomNav = null;
        this.commandPalette = null;
        this.assistantPanel = null;
        this._overlayEl = null;
        this._forcePublic = false;
        this._renderedAsShell = false;
    }

    get isAuthenticated() {
        return !!api.getToken();
    }

    get showAppShell() {
        return this.isAuthenticated && !this._forcePublic;
    }

    template() {
        if (!this.showAppShell) {
            return `<main class="app-content app-content--fullpage" id="content"></main>`;
        }

        return `
            <div class="sidebar-overlay" id="sidebarOverlay"></div>
            <div id="sidebarContainer"></div>
            <div class="app-main">
                <div id="headerContainer"></div>
                <div id="billingBanner"></div>
                <main class="app-content" id="content"></main>
            </div>
            <div id="bottomNavContainer"></div>
            <div id="commandPaletteContainer"></div>
            <div id="assistantPanelContainer"></div>
        `;
    }

    afterRender() {
        this._destroyChildren();
        this._renderedAsShell = this.showAppShell;

        if (!this.showAppShell) return;

        // Sidebar
        const sidebarContainer = this.el.querySelector('#sidebarContainer');
        if (sidebarContainer) {
            this.sidebar = new Sidebar();
            this.sidebar.mount(sidebarContainer);
            // Replace container with sidebar el directly for proper CSS
            sidebarContainer.replaceWith(this.sidebar.el);
        }

        // Header
        const headerContainer = this.el.querySelector('#headerContainer');
        if (headerContainer) {
            this.header = new Header();
            this.header.mount(headerContainer);
            headerContainer.replaceWith(this.header.el);
        }

        // Bottom nav
        const bottomNavContainer = this.el.querySelector('#bottomNavContainer');
        if (bottomNavContainer) {
            this.bottomNav = new BottomNav();
            this.bottomNav.mount(bottomNavContainer);
            bottomNavContainer.replaceWith(this.bottomNav.el);
        }

        // Command palette
        const cpContainer = this.el.querySelector('#commandPaletteContainer');
        if (cpContainer) {
            this.commandPalette = new CommandPalette();
            this.commandPalette.mount(cpContainer);
        }

        // Assistant panel
        const assistantContainer = this.el.querySelector('#assistantPanelContainer');
        if (assistantContainer) {
            this.assistantPanel = new AssistantPanel();
            this.assistantPanel.mount(assistantContainer);
        }

        // Billing banner
        this._loadBillingStatus();

        // Sidebar overlay
        this._overlayEl = this.el.querySelector('#sidebarOverlay');
        if (this._overlayEl) {
            this._overlayEl.addEventListener('click', () => {
                this._closeSidebar();
            });
        }

        // Sidebar toggle events
        this._unsubToggle = eventBus.on('sidebar:toggle', () => {
            if (this.sidebar) {
                this.sidebar.toggle();
                if (this._overlayEl) {
                    this._overlayEl.classList.toggle('is-visible');
                }
            }
        });

        // Close sidebar on navigation
        this._unsubNav = () => this._closeSidebar();
        window.addEventListener('hashchange', this._unsubNav);
    }

    async _loadBillingStatus() {
        const bannerEl = this.el.querySelector('#billingBanner');
        if (!bannerEl) return;

        // Don't check for free plans that haven't subscribed yet — they'll see it in billing section
        // Only show banner if user has permission to manage billing
        const role = store.account?.role;
        if (!role || role === 'client') return;

        // Check at most once per 5 minutes (with TTL)
        const cacheKey = 'bwc_billing_status';
        const cacheTsKey = 'bwc_billing_status_ts';
        const dismissKey = 'bwc_billing_banner_dismissed';
        const cacheTtl = 5 * 60 * 1000; // 5 minutes
        const cached = sessionStorage.getItem(cacheKey);
        const cachedTs = parseInt(sessionStorage.getItem(cacheTsKey) || '0');
        let status;

        if (cached && (Date.now() - cachedTs) < cacheTtl) {
            try { status = JSON.parse(cached); } catch { /* ignore */ }
        }

        if (!status) {
            try {
                status = await api.get('/billing/status');
                sessionStorage.setItem(cacheKey, JSON.stringify(status));
                sessionStorage.setItem(cacheTsKey, String(Date.now()));
            } catch { return; }
        }

        if (!status) return;

        // Determine if we should show banner
        const { plan_slug, plan_name, days_left, limits_reached, cancel_pending, is_trial, trial_days_left } = status;
        const isFree = plan_slug === 'free';
        const showTrial = is_trial && trial_days_left !== null && trial_days_left <= 7;
        const showDays = !isFree && !is_trial && days_left !== null && days_left <= 7 && !cancel_pending;
        const showLimits = limits_reached && limits_reached.length > 0;

        if (!showDays && !showLimits && !showTrial) {
            bannerEl.innerHTML = '';
            return;
        }

        // Check if dismissed this session for this exact state
        const stateKey = `${days_left}_${(limits_reached || []).join(',')}`;
        if (sessionStorage.getItem(dismissKey) === stateKey) {
            bannerEl.innerHTML = '';
            return;
        }

        let message = '';
        let type = 'warning'; // warning or danger

        if (showTrial && trial_days_left <= 0) {
            message = 'Бесплатный пробный период закончился.';
            type = 'danger';
        } else if (showTrial && trial_days_left <= 3) {
            message = `Пробный период заканчивается через ${trial_days_left} ${this._pluralDays(trial_days_left)}.`;
            type = 'danger';
        } else if (showTrial) {
            message = `Пробный период: осталось ${trial_days_left} ${this._pluralDays(trial_days_left)}. Полный доступ ко всем функциям.`;
            type = 'info';
        } else if (showDays && days_left <= 0) {
            message = `Подписка «${escapeHtml(plan_name)}» истекла.`;
            type = 'danger';
        } else if (showDays && days_left <= 3) {
            message = `Подписка «${escapeHtml(plan_name)}» истекает через ${days_left} ${this._pluralDays(days_left)}.`;
            type = 'danger';
        } else if (showDays) {
            message = `До окончания подписки «${escapeHtml(plan_name)}» осталось ${days_left} ${this._pluralDays(days_left)}.`;
        }

        if (showLimits) {
            const labels = limits_reached.map(k => LIMIT_LABELS[k] || k).join(', ');
            const limitMsg = `Достигнут лимит: ${labels}.`;
            message = message ? `${message} ${limitMsg}` : limitMsg;
            type = 'danger';
        }

        bannerEl.innerHTML = `
            <div class="billing-banner billing-banner--${type}">
                <div class="billing-banner__content">
                    <svg class="billing-banner__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
                    </svg>
                    <span class="billing-banner__text">${message}</span>
                    <a href="#/settings?tab=billing" class="billing-banner__action">${showLimits ? 'Сменить тариф' : 'Продлить'}</a>
                </div>
                <button class="billing-banner__close" data-dismiss-banner title="Закрыть">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
            </div>
        `;

        bannerEl.querySelector('[data-dismiss-banner]')?.addEventListener('click', () => {
            sessionStorage.setItem(dismissKey, stateKey);
            bannerEl.innerHTML = '';
        });
    }

    _pluralDays(n) {
        const abs = Math.abs(n) % 100;
        const n1 = abs % 10;
        if (abs > 10 && abs < 20) return 'дней';
        if (n1 > 1 && n1 < 5) return 'дня';
        if (n1 === 1) return 'день';
        return 'дней';
    }

    _closeSidebar() {
        if (this.sidebar) this.sidebar.close();
        if (this._overlayEl) this._overlayEl.classList.remove('is-visible');
    }

    _destroyChildren() {
        if (this.sidebar) { this.sidebar.destroy(); this.sidebar = null; }
        if (this.header) { this.header.destroy(); this.header = null; }
        if (this.bottomNav) { this.bottomNav.destroy(); this.bottomNav = null; }
        if (this.commandPalette) { this.commandPalette.destroy(); this.commandPalette = null; }
        if (this.assistantPanel) { this.assistantPanel.destroy(); this.assistantPanel = null; }
        if (this._unsubToggle) this._unsubToggle();
        if (this._unsubNav) window.removeEventListener('hashchange', this._unsubNav);
    }

    getContentEl() {
        return this.el.querySelector('#content');
    }

    mount(container) {
        this.render();
        if (typeof container === 'string') {
            container = document.querySelector(container);
        }
        container.replaceWith(this.el);
        return this;
    }

    updateLayout() {
        this.render();
    }

    destroy() {
        this._destroyChildren();
        super.destroy();
    }
}
